import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Announcement,
  CreateAnnouncementData,
  getAllAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
} from '../services/announcementService';
import { formatDate } from '../utils/dateUtils';
import '../styles/ListPages.css';
import '../styles/FormPages.css';
import '../styles/AnnouncementsPage.css';

const ANNOUNCEMENT_TYPES = ['Deadline', 'Audit', 'Visit', 'Event', 'General'] as const;
const PRIORITY_LEVELS = ['High', 'Medium', 'Low'] as const;
const TARGET_ROLES = ['All', 'Admin', 'Supervisor', 'Success Coach', 'Viewer'] as const;

const TYPE_ICONS: Record<string, string> = {
  Deadline: '📅',
  Audit: '🔍',
  Visit: '🏢',
  Event: '🎉',
  General: 'ℹ️'
};

const PRIORITY_COLORS: Record<string, string> = {
  High: '#dc3545',
  Medium: '#fd7e14',
  Low: '#0d6efd'
};

export function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);

  const [formData, setFormData] = useState<CreateAnnouncementData>({
    title: '',
    message: '',
    announcementType: 'General',
    priority: 'Medium',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    targetRole: 'All'
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const data = await getAllAnnouncements();
      setAnnouncements(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load announcements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingAnnouncement) {
        await updateAnnouncement(editingAnnouncement.announcementID, formData);
        setSuccess('Announcement updated successfully');
      } else {
        await createAnnouncement(formData);
        setSuccess('Announcement created successfully');
      }

      resetForm();
      fetchAnnouncements();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save announcement');
    }
  };

  const handleEdit = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      message: announcement.message,
      announcementType: announcement.announcementType,
      priority: announcement.priority,
      startDate: announcement.startDate ? announcement.startDate.split('T')[0] : '',
      endDate: announcement.endDate ? announcement.endDate.split('T')[0] : '',
      targetRole: announcement.targetRole
    });
    setShowForm(true);
    setError('');
  };

  const handleDelete = async (announcementID: number) => {
    if (!confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await deleteAnnouncement(announcementID);
      setSuccess('Announcement deleted successfully');
      fetchAnnouncements();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete announcement');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingAnnouncement(null);
    setFormData({
      title: '',
      message: '',
      announcementType: 'General',
      priority: 'Medium',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      targetRole: 'All'
    });
    setError('');
  };

  // Using imported formatDate from utils/dateUtils with 'N/A' fallback
  const formatDateNA = (dateString?: string) => formatDate(dateString, 'N/A');

  const isExpired = (endDate?: string) => {
    if (!endDate) {return false;}
    return new Date(endDate) < new Date();
  };

  if (loading && announcements.length === 0) {
    return <div className="loading">Loading announcements...</div>;
  }

  return (
    <div className="list-page announcements-page">
      <div className="page-header">
        <h2>Announcements Management</h2>
        <p>Create and manage system announcements for staff</p>
      </div>

      <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/admin">
          <button className="button-secondary">
            👥 User Management
          </button>
        </Link>
        <Link to="/admin/config">
          <button className="button-secondary">
            ⚙️ System Configuration
          </button>
        </Link>
        <Link to="/admin/audit-logs">
          <button className="button-secondary">
            📋 Audit Logs
          </button>
        </Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && (
        <div className="alert alert-success" style={{ background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724' }}>
          {success}
        </div>
      )}

      {!showForm && (
        <div className="list-actions">
          <button
            className="button-primary"
            onClick={() => setShowForm(true)}
          >
            + Create Announcement
          </button>
        </div>
      )}

      {showForm && (
        <div className="card announcement-form-card" style={{ marginBottom: '2rem' }}>
          <h3>{editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter announcement title"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Enter announcement message"
                  rows={4}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="announcementType">Type *</label>
                  <select
                    id="announcementType"
                    name="announcementType"
                    value={formData.announcementType}
                    onChange={handleInputChange}
                    required
                  >
                    {ANNOUNCEMENT_TYPES.map(type => (
                      <option key={type} value={type}>
                        {TYPE_ICONS[type]} {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="priority">Priority *</label>
                  <select
                    id="priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                    required
                  >
                    {PRIORITY_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="targetRole">Target Audience *</label>
                  <select
                    id="targetRole"
                    name="targetRole"
                    value={formData.targetRole}
                    onChange={handleInputChange}
                    required
                  >
                    {TARGET_ROLES.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startDate">Start Date *</label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate">End Date (Optional)</label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                  />
                  <small className="form-hint">Leave empty for no expiration</small>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button type="submit" className="button-primary">
                {editingAnnouncement ? 'Update Announcement' : 'Create Announcement'}
              </button>
              <button type="button" onClick={resetForm} className="button-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Title</th>
              <th>Priority</th>
              <th>Target</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Created By</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {announcements.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                  No announcements found. Click &quot;Create Announcement&quot; to add one.
                </td>
              </tr>
            ) : (
              announcements.map(announcement => (
                <tr key={announcement.announcementID} className={isExpired(announcement.endDate) ? 'expired' : ''}>
                  <td>
                    <span className="type-badge">
                      {TYPE_ICONS[announcement.announcementType]} {announcement.announcementType}
                    </span>
                  </td>
                  <td>
                    <strong>{announcement.title}</strong>
                    <div className="message-preview">{announcement.message}</div>
                  </td>
                  <td>
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: PRIORITY_COLORS[announcement.priority] }}
                    >
                      {announcement.priority}
                    </span>
                  </td>
                  <td>{announcement.targetRole}</td>
                  <td>{formatDateNA(announcement.startDate)}</td>
                  <td>{formatDateNA(announcement.endDate)}</td>
                  <td>{announcement.createdByName || 'System'}</td>
                  <td>
                    {!announcement.isActive ? (
                      <span className="status-badge status-inactive">Inactive</span>
                    ) : isExpired(announcement.endDate) ? (
                      <span className="status-badge status-expired">Expired</span>
                    ) : (
                      <span className="status-badge status-active">Active</span>
                    )}
                  </td>
                  <td className="actions">
                    <button
                      className="button-link"
                      onClick={() => handleEdit(announcement)}
                    >
                      Edit
                    </button>
                    <button
                      className="button-link"
                      onClick={() => handleDelete(announcement.announcementID)}
                      style={{ color: '#dc3545' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AnnouncementsPage;
